"use client";

import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Bot, DatabaseZap, LoaderCircle, RefreshCw, ShieldCheck, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { resolveRunyiStatus, runyiStatusSoftSurfaceClasses } from "@/lib/status-indicator";
import type { MemberProjectRuntimeProfile } from "./member-data-auth-panel";

type TenantProfile = {
  configured?: boolean;
  tenant_project_ref?: string;
  enrollment_status?: string;
  runyi_management_status?: string;
  runyi_management_scopes?: string[];
  schema_version?: string | null;
};

export function MemberManagementPanel({
  controlSupabase,
  centralEmail,
  profile,
  compact = false,
}: {
  controlSupabase: SupabaseClient | null;
  centralEmail: string | null;
  profile: MemberProjectRuntimeProfile | null;
  compact?: boolean;
}) {
  const [tenantProfile, setTenantProfile] = useState<TenantProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [advisorSummary, setAdvisorSummary] = useState<string | null>(null);

  const ready = Boolean(
    controlSupabase
    && centralEmail
    && profile?.configured
    && profile.connection_status === "VERIFIED",
  );

  const load = useCallback(async () => {
    if (!controlSupabase || !centralEmail || !profile?.configured) {
      setTenantProfile(null);
      return;
    }
    const { data, error } = await controlSupabase.rpc("runyi_tenant_profile");
    if (error) {
      setNotice(error.message);
      return;
    }
    setTenantProfile((data ?? null) as TenantProfile | null);
  }, [centralEmail, controlSupabase, profile?.configured]);

  useEffect(() => {
    void load();

    const params = new URLSearchParams(window.location.search);
    const result = params.get("runyi_management");
    if (result === "connected") {
      setNotice("RUNYI Supabase management authorization completed. Verifying the connection…");
      window.setTimeout(() => void load(), 500);
      params.delete("runyi_management");
      params.delete("project_ref");
      const query = params.toString();
      window.history.replaceState({}, document.title, window.location.pathname + (query ? `?${query}` : ""));
    } else if (result === "error") {
      setNotice("RUNYI Supabase management authorization was not completed.");
    }
  }, [load]);

  async function connectRunyiManagement() {
    if (!controlSupabase || !ready) return;
    setBusy(true);
    setNotice(null);
    try {
      const { data, error } = await controlSupabase.functions.invoke("runyi-management-oauth-start", { body: {} });
      if (error) throw error;
      if (data?.error) {
        const detail = String(data?.detail ?? data.error);
        if (data.error === "RUNYI_SUPABASE_OAUTH_NOT_CONFIGURED") {
          throw new Error(`RUNYI management OAuth app setup is still required. ${detail}`);
        }
        throw new Error(detail);
      }
      if (!data?.authorization_url) throw new Error("Supabase authorization URL was not returned.");
      window.location.assign(String(data.authorization_url));
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not start RUNYI Supabase management authorization.");
      setBusy(false);
    }
  }

  async function testManagement() {
    if (!controlSupabase) return;
    setBusy(true);
    setNotice(null);
    try {
      const { data, error } = await controlSupabase.functions.invoke("runyi-management-gateway", {
        body: { operation: "project_health" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data?.detail ?? data.error));
      setNotice("RUNYI management channel reached this member Supabase project successfully.");
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "RUNYI management health check failed.");
    } finally {
      setBusy(false);
    }
  }

  async function runSecurityAdvisor() {
    if (!controlSupabase) return;
    setBusy(true);
    setNotice(null);
    try {
      const { data, error } = await controlSupabase.functions.invoke("runyi-management-gateway", {
        body: { operation: "security_advisors" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data?.detail ?? data.error));
      const result = data?.result;
      const findings = Array.isArray(result)
        ? result
        : Array.isArray(result?.lints)
          ? result.lints
          : Array.isArray(result?.advisors)
            ? result.advisors
            : [];
      setAdvisorSummary(`Security Advisor returned ${findings.length} finding${findings.length === 1 ? "" : "s"}.`);
      setNotice("Supabase Security Advisor check completed through the RUNYI management channel.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Security Advisor check failed.");
    } finally {
      setBusy(false);
    }
  }

  async function setChatGptStatus(status: "MEMBER_CONTROLLED" | "NOT_CONNECTED") {
    if (!controlSupabase) return;
    setBusy(true);
    setNotice(null);
    const { data, error } = await controlSupabase.functions.invoke("runyi-member-control", {
      body: {
        operation: "member_chatgpt_status",
        status,
        metadata: { recorded_from: "RUNYI_SETTINGS" },
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);
    if (errorMessage) setNotice(errorMessage);
    else {
      setNotice(
        status === "MEMBER_CONTROLLED"
          ? "Personal ChatGPT management marked as member-controlled. RUNYI does not store that ChatGPT/Supabase plugin token."
          : "Personal ChatGPT management marked as not connected.",
      );
      await load();
    }
    setBusy(false);
  }

  const runyiAuthorized = tenantProfile?.runyi_management_status === "AUTHORIZED";
  const managementStatus = runyiAuthorized ? "AUTHORIZED" : "REQUIRED";
  const managementPresentation = resolveRunyiStatus(managementStatus);

  if (compact) {
    if (!profile?.configured) {
      return (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-background/60 px-3 py-2.5">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground/68">Supabase authorization</p>
            <p className="mt-0.5 text-[10px] leading-4 text-foreground/36">Waiting for the registered member project.</p>
          </div>
          <Badge status="PENDING" className="shrink-0 text-[10px]">PENDING</Badge>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex min-w-0 items-center justify-between gap-3 rounded-xl bg-background/60 px-3 py-2.5">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground/70">
              {runyiAuthorized ? "Supabase authorized" : "Authorize the registered Supabase project"}
            </p>
            <p className="mt-0.5 truncate font-mono text-[10px] text-foreground/34">
              {profile.project_ref ?? "member project"}
            </p>
          </div>
          <Badge status={managementStatus} className="shrink-0 text-[10px]">
            {managementStatus}
          </Badge>
        </div>

        <Button
          onClick={runyiAuthorized ? testManagement : connectRunyiManagement}
          disabled={busy || !ready}
          className="h-9 w-full rounded-xl text-xs font-semibold"
        >
          {busy ? <LoaderCircle className="size-3.5 animate-spin" /> : runyiAuthorized ? <RefreshCw className="size-3.5" /> : <ShieldCheck className="size-3.5" />}
          {runyiAuthorized ? "Verify authorization" : "Open Supabase authorization"}
        </Button>

        {notice && (
          <p className="px-1 text-[10px] leading-4 text-foreground/42">{notice}</p>
        )}
      </div>
    );
  }

  if (!profile?.configured) return null;

  return (
    <Card className="border-foreground/10 bg-card py-0">
      <CardContent className="space-y-4 p-4 sm:space-y-5 sm:p-7">
        <div className="flex items-start gap-4">
          <div className={`grid size-8 shrink-0 place-items-center rounded-lg ${runyiStatusSoftSurfaceClasses[managementPresentation.tone]}`}>
            <DatabaseZap className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">Supabase management</p>
              <Badge status={managementStatus}>
                RUNYI {runyiAuthorized ? "AUTHORIZED" : "NOT CONNECTED"}
              </Badge>
            </div>
            <p className="mt-1 text-sm leading-6 text-foreground/48">
              RUNYI and your personal ChatGPT use independent Supabase authorizations. RUNYI management credentials stay server-side in encrypted Vault storage; your ChatGPT plugin credentials remain controlled only by your ChatGPT account.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4">
            <div className="flex items-center gap-2">
              <Wrench className="size-4 text-primary" />
              <p className="text-sm font-semibold">RUNYI management</p>
            </div>
            <p className="mt-2 text-xs leading-5 text-foreground/45">
              {runyiAuthorized
                ? "Independent Supabase Management API authorization is active for this registered member project."
                : "Authorize RUNYI through Supabase OAuth so the trusted backend can install migrations, repair schema/RLS/grants, inspect health and run security checks."}
            </p>
            <Button
              onClick={runyiAuthorized ? testManagement : connectRunyiManagement}
              disabled={busy || !ready}
              className="mt-4 h-11 w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90"
            >
              {busy ? <LoaderCircle className="animate-spin" /> : runyiAuthorized ? <RefreshCw /> : <ShieldCheck />}
              {runyiAuthorized ? "Test RUNYI management" : "Connect RUNYI to Supabase"}
            </Button>
            {runyiAuthorized && (
              <Button
                variant="outline"
                onClick={runSecurityAdvisor}
                disabled={busy}
                className="mt-2 h-11 w-full border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground"
              >
                <ShieldCheck />Run Security Advisor
              </Button>
            )}
          </div>

          <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4">
            <div className="flex items-center gap-2">
              <Bot className="size-4 text-primary" />
              <p className="text-sm font-semibold">Personal ChatGPT management</p>
            </div>
            <p className="mt-2 text-xs leading-5 text-foreground/45">
              Connect the member's Supabase account directly to their own ChatGPT Supabase plugin. RUNYI records only the connection state—not the ChatGPT OAuth token.
            </p>
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                onClick={() => void setChatGptStatus("MEMBER_CONTROLLED")}
                disabled={busy || !ready}
                className="h-11 flex-1 border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground"
              >
                Mark connected
              </Button>
              <Button
                variant="outline"
                onClick={() => void setChatGptStatus("NOT_CONNECTED")}
                disabled={busy || !ready}
                className="h-11 flex-1 border-foreground/12 bg-transparent text-foreground hover:bg-foreground/8 hover:text-foreground"
              >
                Clear status
              </Button>
            </div>
          </div>
        </div>

        {advisorSummary && <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4 text-sm text-foreground/60">{advisorSummary}</div>}
        {notice && <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-100">{notice}</div>}
      </CardContent>
    </Card>
  );
}
